import * as React from "react";
import * as THREE from 'three';
import {Viewer} from './js/main';
import {ContextMenu} from "./context-menu";
import {LayerSwitcher} from "./layer-switcher";
import {useEffect, useRef, useState} from "react";
import {createStyles, makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import {changeMeasureUnits, selectData, selectView} from '../redux/projectionsSlice';
import {useSelector, useDispatch} from 'react-redux';
import {selectCameraFiles, selectPointClouds, selectOrthos} from "../redux/assetsSlice";
import {headerHeight} from "../app";
import LocalScene from "./js/projections";
import {
  selectName,
  selectProjectModified
} from "../redux/projectSlice";
import {assetDrawerWidth, assetDrawerHeight} from "../asset-drawer";
import {Button} from "@material-ui/core";
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import {changeAvailableClassifications, changeSwitched,
  selectViewerSwitched} from "../redux/settingsSlice";
import { CursorTooltip } from "../cursor-tooltip";
import { AlertDialog } from "../dialogs";
import {isDevMode} from '../electron-modules';
import { useSnackbar } from 'notistack';
import {
  AerialViewState,
  changeCurrentCamera,
  changeSavedAerialState,
  selectBasicAdjustments,
  selectCameraHeight,
  selectCameraTransform,
  selectCurrentCamera,
  selectSavedAerialState
} from "../redux/cameraSlice";
import { readTransformFile } from "../camera-utils";
import proj4 from 'proj4';
import {register} from 'ol/proj/proj4';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    parent: {
      flex: 1
    },
    large: {
      width: '100%',
      height: `calc(100% - ${headerHeight}px)`,
      position: 'fixed'
    },
    small: {
      width: `${assetDrawerWidth}px`,
      height: `${100-assetDrawerHeight}%`,
      position: 'absolute',
      background: 'white',
      margin: '0.5em',
      border: '1px solid',
      zIndex: 1
    },
    offsetSmall: {
      bottom: theme.spacing(0),
      margin: theme.spacing(0),
      borderTop: "1px solid black",
      zIndex: theme.zIndex.modal,
      border: "none"
    },
    offsetLarge: {
      marginLeft: assetDrawerWidth,
      width: `calc(100% - ${assetDrawerWidth}px)`,
    },
    gradient: {
      'background-image': 'linear-gradient(45deg, #fdfcfb 0%, #e2d1c3 100%);'
    },
    buttonParent: {
      padding: "3px",
      background: "rgba(255,255,255,0.4)",
      position: 'absolute',
      top: "0.5em",
      left: "0.5em",
      "z-index": "10",
      "border-radius": "5px",
      "-webkit-transition": "-webkit-transform .3s ease-in-out",
      "transition": "transform .3s ease-in-out",
      "&:hover": {
        "-webkit-transform": "rotate(-180deg)",
        transform: "rotate(-180deg)"
      }
    },
    button: {
      padding: "0px",
      background: "rgba(0,60,136,0.5)",
      color: "white",
      "min-width": "24px",
      "&:hover": {
        background: "rgba(0,60,136,0.7)",
      }
    }
  }),
);

const ViewSwitcher = props => {
  const {classes, visible, handleSwitched} = props;

  return (
    <React.Fragment>
      {visible && (<div className={classes.buttonParent}>
        <Button
          className={classes.button}
          onClick={handleSwitched}
        >
          <RotateLeftIcon/>
        </Button>
      </div>)}
    </React.Fragment>
  );
};

export const SceneViewer = (props) => {
  const {viewer, setViewer, assetDrawerState, setAddObservations,
    setMeasurements, setViewerTooltipText, setAerialTooltipText,
    viewerTooltipText, aerialTooltipText, resetObservationType,
    setResetAlignerManager, clearTooltipText, setSingleAlignment} = props;

  const theme = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const showMessage = useSnackbar().enqueueSnackbar;

  const orthos = useSelector(selectOrthos);
  const pointClouds = useSelector(selectPointClouds);
  const camerasFiles = useSelector(selectCameraFiles);
  const dataProjection = useSelector(selectData);
  const viewProjection = useSelector(selectView);
  const currentCamera = useSelector(selectCurrentCamera);
  const cameraTransformPath = useSelector(selectCameraTransform);
  const projectModifiedDate = useSelector(selectProjectModified);
  const projectName = useSelector(selectName);
  const viewerSwitched = useSelector(selectViewerSwitched);
  const cameraHeight = useSelector(selectCameraHeight);
  const basicAdjustments = useSelector(selectBasicAdjustments);
  const savedAerialState = useSelector(selectSavedAerialState);

  const divRef = useRef(null);
  const mapRef = useRef(null);

  const [customAlertHTML, setCustomAlertHTML] = useState(null);
  const [contextState, setContextState] = useState({
    mouseX: null,
    mouseY: null,
  });
  const [aerialState, setAerialState] = useState({
    extent: null,
    rotation: null,
  } as AerialViewState);

  const onProjectLoad = () => {
    /** Ran after new project is created or project is loaded */
    viewer?.setResetCameraAligner(true);
    viewer?.resetAerialMap(savedAerialState);
  };

  const setCurrentCamera = (payload) => {
    dispatch(changeCurrentCamera(payload));
  };

  const contextMenuClick = (event) => {
    event.preventDefault();
    setContextState({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const getClasses = (switched) => {
    let mapClasses = [];
    let viewerClasses = [classes.gradient];

    const classLarge = [
      classes.large,
      assetDrawerState ? classes.offsetLarge : ""
    ];
    const classSmall = [
      classes.small,
      assetDrawerState ? classes.offsetSmall : ""
    ];

    if (switched) {
      mapClasses = [...mapClasses, ...classLarge];
      viewerClasses = [...viewerClasses, ...classSmall];
    } else {
      mapClasses = [...mapClasses, ...classSmall];
      viewerClasses = [...viewerClasses, ...classLarge];
    }

    return {
      viewerClasses: viewerClasses.join(" "),
      mapClasses: mapClasses.join(" ")
    };
  };

  const getCurrentCameraID = () => {
    let currentCameraID = "";
    if (camerasFiles.length > 0) {
      currentCameraID = currentCamera ? currentCamera : camerasFiles[0].id;
    }

    return currentCameraID;
  };

  const handleSwitched = () => {
    dispatch(changeSwitched(!viewerSwitched));
  };

  const triggerWindowResize = () => {
    window.dispatchEvent(new Event('resize'));
  };

  useEffect(() => {
    onProjectLoad();
  }, [projectModifiedDate]);

  useEffect(() => {
    if (!aerialState.extent || !viewer) {
      return;
    }

    dispatch(changeSavedAerialState(aerialState));
  }, [viewer, aerialState]);

  useEffect(() => {
    viewer?.setProjectName(projectName);
  }, [viewer, projectName]);

  useEffect(() => {
    clearTooltipText();
    triggerWindowResize();
    if (viewerSwitched) {
      viewer?.removeInteractions();
    }
  }, [viewerSwitched]);

  useEffect(() => {
    triggerWindowResize();
  }, [assetDrawerState]);

  useEffect(() => {
    viewer?.setActiveCamera(currentCamera);
  }, [viewer, currentCamera]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (viewer) {
        const classifications = viewer.getAvailableClassifications();
        dispatch(changeAvailableClassifications(classifications));
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [viewer, dispatch]);

  useEffect(() => {
    const viewerProps = {
      contextMenuClick,
      setCustomAlertHTML,
      setMeasurements,
      setAerialState,
      setSingleAlignment,
      setViewerTooltipText,
      setAerialTooltipText,
      setAddObservations,
      resetObservationType,
      setResetAlignerManager,
      showMessage,
      setCurrentCamera,
    };

    let viewerRef = new Viewer(divRef.current, mapRef.current, viewerProps);
    setViewer(viewerRef);

    if (isDevMode) {
      // Temporary global for easy access
      window['viewer'] = viewerRef;
      window['local'] = LocalScene;
      window['THREE'] = THREE;
    }

    return () => {
      console.log("Destroy viewer");
      viewerRef.destroy();
    };
  }, []);

  useEffect(() => {
    // Register current projection for openlayers to use
    proj4.defs(dataProjection.name, dataProjection.string);
    register(proj4);

    // Clear previous asset data
    viewer?.reconcilePointClouds([]);
    viewer?.reconcileCameras([]);
    viewer?.reconcileOrthos([]);
    viewer?.resetLoadedOnce();

    // Update projection
    viewer?.setDataProjection(dataProjection);
  }, [viewer, dataProjection]);

  useEffect(() => {
    viewer?.setViewProjection(viewProjection);

    const units = LocalScene.viewProjectionUnits;
    dispatch(changeMeasureUnits(units));
  }, [viewer, viewProjection]);

  useEffect(() => {
    if (cameraTransformPath === null) {
      viewer?.resetCameraMatrix();
      return;
    }

    readTransformFile(cameraTransformPath)
      .then(data => {
        const {success, content} = data;

        if (success) {
          viewer?.setCameraMatrix(content);
        } else {
          showMessage(content, {
            variant: "error"
          });
        }
      });
  }, [viewer, cameraTransformPath]);

  useEffect(() => {
    viewer?.setCameraHeights(cameraHeight);
  }, [viewer, cameraHeight]);

  useEffect(() => {
    viewer?.setCameraAdjustments(basicAdjustments);
  }, [viewer, basicAdjustments]);

  useEffect(() => {
    // Add/remove pointclouds
    viewer?.reconcilePointClouds(pointClouds);
  }, [viewer, pointClouds]);

  useEffect(() => {
    // Add/remove orthomosiacs
    viewer?.reconcileOrthos(orthos);
  }, [viewer, orthos]);

  useEffect(() => {
    viewer?.reconcileCameras(camerasFiles, cameraHeight,
      basicAdjustments, getCurrentCameraID());
  }, [viewer, camerasFiles]);

  let {viewerClasses, mapClasses} = getClasses(viewerSwitched);

  return (
    <React.Fragment>
      <div className={classes.parent}>
        {/* Viewer div */}
        <CursorTooltip text={viewerTooltipText}>
          <div className={viewerClasses} ref={divRef}>
            <ViewSwitcher
              classes={classes}
              visible={viewerSwitched}
              handleSwitched={handleSwitched}
            />
            <ContextMenu
              viewer={viewer}
              state={contextState}
              setContextState={setContextState}
              setViewerTooltipText={setViewerTooltipText}
            />
          </div>
        </CursorTooltip>

        {/* Map div */}
        <CursorTooltip text={aerialTooltipText}>
          <div className={mapClasses} ref={mapRef}>
            <ViewSwitcher
              classes={classes}
              visible={!viewerSwitched}
              handleSwitched={handleSwitched}
            />
            <LayerSwitcher
              viewer={viewer}
              setAerialTooltipText={setAerialTooltipText}
            />
          </div>
        </CursorTooltip>
      </div>

      {/* Viewer alert dialog */}
      <AlertDialog
        open={customAlertHTML !== null}
        title="Alert Message"
        handleClose={() => {
          setCustomAlertHTML(null);
        }}
      >
        <div dangerouslySetInnerHTML={{__html: customAlertHTML}}/>
      </AlertDialog>
    </React.Fragment>
  );
};